import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FadeInUp, ScrollFadeIn, ScrollStaggerContainer, StaggerItem } from './MotionWrappers';
import { supabase } from '../supabaseClient';
import '../assets/css/about.css';
import '../assets/css/psearch.css';
import SignInModal from './SignInModal';
import Nav from './Nav';
import SEO from './SEO';

// Instagram Brand Card Component
const InstagramBrandCard = ({ brand }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a 
      href={brand.instagram_url} 
      target="_blank" 
      rel="noopener noreferrer" 
      className="text-decoration-none"
      style={{ display: 'block', height: '100%' }}
    >
      <div 
        style={{ 
          position: 'relative',
          height: '100%',
          minHeight: '200px',
          borderRadius: '0px',
          overflow: 'hidden',
          background: '#faf9f8',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered ? '0 8px 24px rgba(0,0,0,0.12)' : 'none',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Product Image */}
        <div style={{ 
          width: '100%', 
          height: '250px', 
          overflow: 'hidden',
          background: '#fff',
        }}>
          <img 
            src={brand.image || '/default-image.avif'} 
            alt={brand.name}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={(e) => {
              e.target.src = '/default-image.avif';
            }}
          />
        </div>
        
        {/* Hover Overlay */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 0.3s ease',
            pointerEvents: 'none',
          }}
        >
          <p className="yb-brand-name" style={{ fontSize: '18px', fontWeight: '500', color: '#fff', margin: 0, textAlign: 'center', padding: '0 16px' }}>
            {brand.name}
          </p>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            color: '#fff',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
            </svg>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#fff' }}>
              {/* only take the user name from the brand.instagram_handle  https://www.instagram.com/kiwi_ism__/ -> kiwi_ism__ */}
              {brand.instagram_handle}
            </span>
          </div>
        </div>
      </div>
    </a>
  );
};

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://yemberzal-app-28481d1d0b39.herokuapp.com/api'
  : 'http://127.0.0.1:8000/api';

function Brands() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedBrands, setSavedBrands] = useState([]);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInModalMessage, setSignInModalMessage] = useState('');
  const [authEmail, setAuthEmail] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [instagramOnlyBrands, setInstagramOnlyBrands] = useState([]);
  const [instagramBrandsLoading, setInstagramBrandsLoading] = useState(true);
  
  // Filter state
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      if (session?.user) {
        loadSavedBrands(session.user.id);
      }
    };
    getSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadSavedBrands(session.user.id);
      } else {
        setSavedBrands([]);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    fetchBrands();
    fetchInstagramOnlyBrands();
  }, []);

  const fetchInstagramOnlyBrands = async () => {
    try {
      setInstagramBrandsLoading(true);
      const response = await fetch(`${API_BASE_URL}/brands/instagram-only/`);
      if (response.ok) {
        const data = await response.json();
        setInstagramOnlyBrands(data);
      } else {
        console.error('Failed to fetch Instagram brands');
      }
    } catch (error) {
      console.error('Error fetching Instagram-only brands:', error);
    } finally {
      setInstagramBrandsLoading(false);
    }
  };

  // Brands to exclude from display
  const excludedBrands = ['Shop Namastay', 'Baraqah', 'Koshur', 'Ethnic Wears'];
  
  // Display name mappings (original brand -> display name)
  const brandDisplayNames = {
    'tulpalav': 'Tul Palav',
    'Tulpalav': 'Tul Palav',
  };

  const getDisplayName = (brand) => brandDisplayNames[brand] || brand;

  const fetchBrands = async () => {
    try {
      setError(null);
      const response = await fetch(`${API_BASE_URL}/brands/with-images/`);
      if (response.ok) {
        const data = await response.json();
        
        // Filter out excluded brands and trim names
        const seen = new Set();
        const filteredBrands = data
          .map(brand => ({
            ...brand,
            name: brand.name ? brand.name.trim() : brand.name
          }))
          .filter(brand => {
            if (!brand.name) return false;
            const normalizedName = brand.name.toLowerCase().trim();
            if (seen.has(normalizedName)) return false;
            if (excludedBrands.some(excluded => excluded.toLowerCase() === normalizedName)) return false;
            seen.add(normalizedName);
            return true;
          });
        
        setBrands(filteredBrands);
      } else {
        setError('Failed to fetch brands');
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      setError('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  // Sort brands
  const filteredBrands = React.useMemo(() => {
    let filtered = brands;
    
    // Sort
    if (sortBy === 'name') {
      filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'products') {
      filtered = [...filtered].sort((a, b) => (b.product_count || 0) - (a.product_count || 0));
    }
    
    return filtered;
  }, [brands, sortBy]);

  const loadSavedBrands = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('saved_brands')
        .select('brand_name')
        .eq('user_id', userId);
      
      if (data && !error) {
        setSavedBrands(data.map(item => item.brand_name));
      }
    } catch (error) {
      console.error('Error loading saved brands:', error);
    }
  };

  const toggleSaveBrand = async (brandName) => {
    if (!user) {
      setSignInModalMessage('Sign in to bookmark your favorite brands!');
      setShowSignInModal(true);
      return;
    }

    const isSaved = savedBrands.includes(brandName);

    try {
      if (isSaved) {
        await supabase
          .from('saved_brands')
          .delete()
          .eq('user_id', user.id)
          .eq('brand_name', brandName);
        setSavedBrands(savedBrands.filter(b => b !== brandName));
      } else {
        await supabase
          .from('saved_brands')
          .insert({ user_id: user.id, brand_name: brandName });
        setSavedBrands([...savedBrands, brandName]);
      }
    } catch (error) {
      console.error('Error toggling saved brand:', error);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) {
        setAuthMessage(`Error: ${error.message}`);
      }
    } catch (err) {
      setAuthMessage(`Error: ${err.message}`);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    if (!authEmail) return;

    setAuthLoading(true);
    setAuthMessage('');

    const { error } = await supabase.auth.signInWithOtp({
      email: authEmail,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email for the magic link!');
    }

    setAuthLoading(false);
  };

  const handleEmailPasswordSignIn = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setShowSignInModal(false);
    }
    setAuthLoading(false);
  };

  const handleEmailPasswordSignUp = async (email, password) => {
    setAuthLoading(true);
    setAuthMessage('');
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { user_role: 'general' },
      },
    });
    
    if (error) {
      setAuthMessage(`Error: ${error.message}`);
    } else {
      setAuthMessage('Check your email to confirm your account!');
    }
    setAuthLoading(false);
  };

  const scrollToTopAndNavigate = (brandName) => {
    window.scrollTo(0, 0);
    navigate(`/brand/${encodeURIComponent(brandName)}`);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setShowUserDropdown(false);
  };

  return (
    <>
    <div className="container py-5 font-raleway">
      <SEO
        title="Kashmiri Brands — Discover Authentic Fashion Labels"
        description="Explore 50+ authentic Kashmiri fashion brands. From pashmina artisans to modern Koshur designers — discover the brands preserving Kashmir's fashion heritage."
        path="/brands"
      />
      <Nav />

      {/* Hero Header */}
      <div className="brands-hero page-hero-fullwidth" style={{
        background: 'linear-gradient(135deg, rgba(248,248,248,0.8) 0%, rgba(255,255,255,0.6) 100%)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        paddingTop: '60px',
        paddingBottom: '50px',
        position: 'relative',
        overflow: 'hidden',
      }}>
          <div className="fluid-blob fluid-blob-3" style={{ opacity: 0.04 }} />
          <FadeInUp delay={0.1}>
          <h1 className="brands-title" style={{
            fontSize: '3rem',
            fontWeight: 600,
            fontFamily: "'Montserrat', sans-serif",
            color: '#1a1a1a',
            marginBottom: '16px',
            letterSpacing: '0em',
            position: 'relative',
            zIndex: 1,
          }}>B R A N D S</h1>
          </FadeInUp>
          <FadeInUp delay={0.25}>
          <p style={{
            fontSize: '1rem',
            fontFamily: 'Montserrat, sans-serif',
            color: '#666',
            maxWidth: '560px',
            lineHeight: 1.6,
            margin: 0,
            position: 'relative',
            zIndex: 1,
          }}>
            Discover authentic Kashmiri fashion from the finest artisan brands and designers.
          </p>
          </FadeInUp>
      </div>

      <Container className="mt-0">
        {/* Filters Row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: '24px',
          padding: '24px 0',
          borderBottom: '1px solid #e5e5e5',
        }}>
          {/* Sort */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', alignItems: 'center' }}>
            <Form.Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                border: '1px solid #d1d1d1',
                borderRadius: '6px',
                padding: '8px 24px 8px 10px',
                fontSize: '13px',
                color: '#1a1a1a',
                background: '#fff',
                minWidth: '130px',
                cursor: 'pointer',
                appearance: 'auto',
              }}
            >
              <option value="name">Sort by: Name</option>
              <option value="products">Sort by: Products</option>
            </Form.Select>
          </div>
        </div>

        {/* Brand count */}
        <div style={{
          padding: '16px 0',
          fontSize: '14px',
          color: '#666',
        }}>
          {!loading && !error && filteredBrands.length > 0 && (
            <span>{filteredBrands.length} brand{filteredBrands.length !== 1 ? 's' : ''}</span>
          )}
        </div>

      {/* Loading State */}
        {loading && (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading brands...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-5">
            <p>{error}</p>
          </div>
        )}

        {/* Brands Grid */}
        {!loading && !error && (
          <>
            {filteredBrands.length === 0 ? (
              <div className="text-center py-5">
                <p>No brands found matching your criteria.</p>
              </div>
            ) : (
              <Row style={{ display: 'flex', gap: '0', justifyContent: 'center' }}>
                {filteredBrands.map((brand, index) => (
                  <Col md={3} key={index} style={{ padding: '0 12px', marginBottom: '24px' }}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: '-30px' }}
                      transition={{ duration: 0.5, delay: (index % 4) * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
                      whileHover={{ y: -6 }}
                      style={{ cursor: 'pointer' }}
                      onClick={() => scrollToTopAndNavigate(brand.name)}
                    >
                      {/* Image */}
                      <div style={{
                        width: '90%',
                        height: '480px',
                        overflow: 'hidden',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5',
                        position: 'relative',
                      }}>
                        <img
                          src={brand.image || '/default-image.avif'}
                          alt={brand.name}
                          loading="lazy"
                          decoding="async"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            objectPosition: 'center top',
                            transition: 'transform 0.3s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.03)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = '/default-image.avif';
                          }}
                        />

                        {/* Heart Save Icon */}
                        <button
                          style={{
                            position: 'absolute',
                            top: '12px',
                            right: '18px',
                            background: 'rgba(255,255,255,0.95)',
                            border: 'none',
                            borderRadius: '50%',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            zIndex: 10,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            transition: 'all 0.2s',
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleSaveBrand(brand.name);
                          }}
                          title={savedBrands.includes(brand.name) ? 'Remove from saved' : 'Save brand'}
                        >
                          <svg
                            width="22"
                            height="22"
                            viewBox="0 0 24 24"
                            fill={savedBrands.includes(brand.name) ? '#e63946' : 'none'}
                            stroke="#e63946"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 21s-7-4.35-7-10A5 5 0 0 1 12 6a5 5 0 0 1 7 5c0 5.65-7 10-7 10z" />
                          </svg>
                        </button>
                      </div>

                      {/* Brand Info */}
                      <div style={{ paddingTop: '14px' }}>
                        <h3 style={{
                          fontSize: '15px',
                          fontWeight: '600',
                          fontFamily: 'Montserrat, sans-serif',
                          color: '#1a1a1a',
                          margin: '0 0 4px 0',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}>
                          {getDisplayName(brand.name)}
                        </h3>
                        <p style={{
                          fontSize: '13px',
                          fontFamily: 'Montserrat, sans-serif',
                          color: '#888',
                          margin: 0,
                        }}>
                          {brand.product_count || 0} {brand.product_count === 1 ? 'product' : 'products'}
                        </p>
                      </div>
                    </motion.div>
                  </Col>
                ))}
              </Row>
            )}
          </>
        )}
      </Container>

      {/* Instagram Brands Section */}
      <section className="py-5">
        <ScrollFadeIn>
        <div className="text-center mb-5">
          <h2 style={{ fontSize: '1.8rem', fontWeight: '300', color: '#1a1a1a', marginBottom: '8px' }}>
            Instagram Brands
          </h2>
          <p style={{ color: '#888', maxWidth: '500px', margin: '0 auto' }}>
            Emerging Kashmiri brands selling exclusively on Instagram
          </p>
        </div>
        </ScrollFadeIn>
        
        {instagramBrandsLoading ? (
          <div className="text-center py-5">
            <p style={{ color: '#888' }}>Loading Instagram brands...</p>
          </div>
        ) : instagramOnlyBrands.length === 0 ? (
          <div className="text-center py-5">
            <p style={{ fontSize: '1.25rem', fontWeight: '500', color: '#1a1a1a' }}>Coming Soon</p>
          </div>
        ) : (
          <div className="row g-3">
            {instagramOnlyBrands.map((brand, index) => (
              <div key={index} className="col-6 col-md-4 col-lg-3 ">
                <InstagramBrandCard brand={brand} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
    <SignInModal
      open={showSignInModal}
      onClose={() => {
        setShowSignInModal(false);
        setAuthMessage('');
        setAuthEmail('');
        setSignInModalMessage('');
      }}
      message={signInModalMessage}
      authEmail={authEmail}
      setAuthEmail={setAuthEmail}
      authLoading={authLoading}
      authMessage={authMessage}
      onEmailSignIn={handleEmailSignIn}
      onGoogleSignIn={handleGoogleSignIn}
      onEmailPasswordSignIn={handleEmailPasswordSignIn}
      onEmailPasswordSignUp={handleEmailPasswordSignUp}
    />
    <style>{`
      @media (max-width: 768px) {
        .brands-hero {
          padding: 40px 16px 30px !important;
        }
        .brands-title {
          font-size: 2rem !important;
        }
      }
      @media (max-width: 480px) {
        .brands-title {
          font-size: 1.5rem !important;
        }
      }
    `}</style>
    </>
  );
}

export default Brands;
